import { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Badge } from '../components/ui/badge';
import { Switch } from '../components/ui/switch';
import { toast } from 'sonner';
import { format, parseISO, isBefore, isToday, addDays } from 'date-fns';
import { Plus, Trash2, Save, Calendar as CalendarIcon, MapPin, Clock, User, Edit2, Stethoscope } from 'lucide-react';
import AttachmentExpander from '../components/AttachmentExpander';
import useAttachmentCounts from '../hooks/useAttachmentCounts';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const SPECIALTIES = [
    'Rheumatologist', 'Neurologist', 'Endocrinologist', 'Gastroenterologist',
    'Dermatologist', 'Ophthalmologist', 'Primary Care', 'Immunologist',
    'Cardiologist', 'Physical Therapist', 'Nutritionist', 'Other'
];

export default function AppointmentTracker() {
    const { token } = useAuth();
    const [appointments, setAppointments] = useState([]);
    const [loading, setLoading] = useState(false);
    const [showAdd, setShowAdd] = useState(false);
    const [editingId, setEditingId] = useState(null);
    
    // Form state
    const [title, setTitle] = useState('');
    const [doctorName, setDoctorName] = useState('');
    const [specialty, setSpecialty] = useState('');
    const [date, setDate] = useState(format(addDays(new Date(), 7), 'yyyy-MM-dd'));
    const [time, setTime] = useState('09:00');
    const [location, setLocation] = useState('');
    const [notes, setNotes] = useState('');
    const [reminder, setReminder] = useState(true);
    const [saving, setSaving] = useState(false);

    const fetchAppointments = useCallback(async () => {
        setLoading(true);
        try {
            const res = await fetch(`${API_URL}/api/appointments?limit=50`, {
                headers: { 'Authorization': `Bearer ${token}` }
            });
            
            if (res.ok) {
                const data = await res.json();
                setAppointments(data || []);
            }
        } catch (error) {
            console.error('Failed to fetch appointments:', error);
        } finally {
            setLoading(false);
        }
    }, [token]);

    useEffect(() => {
        fetchAppointments();
    }, [fetchAppointments]);

    const resetForm = () => {
        setTitle('');
        setDoctorName('');
        setSpecialty('');
        setDate(format(addDays(new Date(), 7), 'yyyy-MM-dd'));
        setTime('09:00');
        setLocation('');
        setNotes('');
        setReminder(true);
        setEditingId(null);
    };

    const saveAppointment = async () => {
        if (!title.trim() || !date || !time) {
            toast.error('Please enter title, date and time');
            return;
        }

        setSaving(true);
        try {
            const url = editingId 
                ? `${API_URL}/api/appointments/${editingId}`
                : `${API_URL}/api/appointments`;
            
            const res = await fetch(url, {
                method: editingId ? 'PUT' : 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${token}`
                },
                body: JSON.stringify({
                    title,
                    doctor_name: doctorName || null,
                    specialty: specialty || null,
                    date,
                    time,
                    location: location || null,
                    notes: notes || null,
                    reminder
                })
            });

            if (res.ok) {
                toast.success(editingId ? 'Appointment updated' : 'Appointment added');
                setShowAdd(false);
                resetForm();
                fetchAppointments();
            } else {
                throw new Error('Failed to save');
            }
        } catch (error) {
            toast.error('Failed to save appointment');
        } finally {
            setSaving(false);
        }
    };

    const deleteAppointment = async (id) => {
        if (!window.confirm('Delete this appointment?')) return;

        try {
            const res = await fetch(`${API_URL}/api/appointments/${id}`, {
                method: 'DELETE',
                headers: { 'Authorization': `Bearer ${token}` }
            });

            if (res.ok) {
                toast.success('Appointment deleted');
                fetchAppointments();
            }
        } catch (error) {
            toast.error('Failed to delete');
        }
    };

    const editAppointment = (apt) => {
        setEditingId(apt.id);
        setTitle(apt.title);
        setDoctorName(apt.doctor_name || '');
        setSpecialty(apt.specialty || '');
        setDate(apt.date);
        setTime(apt.time);
        setLocation(apt.location || '');
        setNotes(apt.notes || '');
        setReminder(apt.reminder);
        setShowAdd(true);
    };

    const upcomingAppointments = appointments.filter(apt => !isBefore(parseISO(apt.date), new Date()) || isToday(parseISO(apt.date)));
    const pastAppointments = appointments.filter(apt => isBefore(parseISO(apt.date), new Date()) && !isToday(parseISO(apt.date)));

    // Bulk-fetch attachment counts for paperclip badges
    const allIds = appointments.map(a => a.id);
    const attachmentCounts = useAttachmentCounts('appointment', allIds);

    const getStatusColor = (dateStr) => {
        const aptDate = parseISO(dateStr);
        if (isToday(aptDate)) return 'bg-emerald-500';
        const daysUntil = Math.ceil((aptDate.getTime() - new Date().getTime()) / (1000 * 60 * 60 * 24));
        if (daysUntil <= 3) return 'bg-amber-500';
        if (daysUntil <= 7) return 'bg-sky-500';
        return 'bg-indigo-500';
    };

    const getTimeUntil = (dateStr) => {
        const aptDate = parseISO(dateStr);
        if (isToday(aptDate)) return 'Today';
        const daysUntil = Math.ceil((aptDate.getTime() - new Date().getTime()) / (1000 * 60 * 60 * 24));
        if (daysUntil === 1) return 'Tomorrow';
        if (daysUntil <= 7) return `In ${daysUntil} days`;
        return format(aptDate, 'MMM d, yyyy');
    };

    return (
        <div className="space-y-6">
            <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
                <div>
                    <h1 className="font-heading text-3xl font-bold">Appointments</h1>
                    <p className="text-muted-foreground mt-1">Manage your medical appointments</p>
                </div>
                <Button onClick={() => { resetForm(); setShowAdd(true); }} data-testid="add-appointment-btn">
                    <Plus className="w-4 h-4 mr-2" />
                    Add Appointment
                </Button>
            </div>

            {upcomingAppointments.length > 0 && (
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                    <Card className="bg-gradient-to-br from-emerald-50 to-teal-50 dark:from-emerald-950/30 dark:to-teal-950/20 border-emerald-200">
                        <CardContent className="p-4">
                            <div className="flex items-center gap-3">
                                <div className="p-2 bg-emerald-500 rounded-lg">
                                    <CalendarIcon className="w-4 h-4 text-white" />
                                </div>
                                <div>
                                    <p className="text-xs text-muted-foreground">Upcoming</p>
                                    <p className="text-xl font-bold">{upcomingAppointments.length}</p>
                                </div>
                            </div>
                        </CardContent>
                    </Card>

                    {upcomingAppointments[0] && (
                        <Card className="col-span-2 md:col-span-3 bg-gradient-to-br from-indigo-50 to-violet-50 dark:from-indigo-950/30 dark:to-violet-950/20">
                            <CardContent className="p-4">
                                <p className="text-xs text-muted-foreground">Next Appointment</p>
                                <p className="font-bold">{upcomingAppointments[0].title}</p>
                                <p className="text-sm">
                                    {getTimeUntil(upcomingAppointments[0].date)} at {upcomingAppointments[0].time}
                                </p>
                            </CardContent>
                        </Card>
                    )}
                </div>
            )}

            <div className="grid lg:grid-cols-3 gap-6">
                <div className="lg:col-span-2 space-y-6">
                    {/* Add/Edit Form */}
                    {showAdd && (
                        <Card className="border-2 border-indigo-200 dark:border-indigo-800">
                            <CardHeader>
                                <CardTitle>{editingId ? 'Edit Appointment' : 'Add Appointment'}</CardTitle>
                            </CardHeader>
                            <CardContent className="space-y-4">
                                <div>
                                    <label className="text-sm font-medium mb-2 block">Title / Reason</label>
                                    <Input
                                        placeholder="e.g., Rheumatology Follow-up"
                                        value={title}
                                        onChange={(e) => setTitle(e.target.value)}
                                        data-testid="appointment-title-input"
                                    />
                                </div>

                                <div className="grid grid-cols-2 gap-4">
                                    <div>
                                        <label className="text-sm font-medium mb-2 block">Doctor Name</label>
                                        <Input
                                            placeholder="Dr. Smith"
                                            value={doctorName}
                                            onChange={(e) => setDoctorName(e.target.value)}
                                        />
                                    </div>
                                    <div>
                                        <label className="text-sm font-medium mb-2 block">Specialty</label>
                                        <select
                                            className="w-full h-10 px-3 rounded-md border bg-background"
                                            value={specialty}
                                            onChange={(e) => setSpecialty(e.target.value)}
                                        >
                                            <option value="">Select specialty</option>
                                            {SPECIALTIES.map(s => (
                                                <option key={s} value={s}>{s}</option>
                                            ))}
                                        </select>
                                    </div>
                                </div>

                                <div className="grid grid-cols-2 gap-4">
                                    <div>
                                        <label className="text-sm font-medium mb-2 block">Date</label>
                                        <Input
                                            type="date"
                                            value={date}
                                            onChange={(e) => setDate(e.target.value)}
                                            data-testid="appointment-date-input"
                                        />
                                    </div>
                                    <div>
                                        <label className="text-sm font-medium mb-2 block">Time</label>
                                        <Input
                                            type="time"
                                            value={time}
                                            onChange={(e) => setTime(e.target.value)}
                                            data-testid="appointment-time-input"
                                        />
                                    </div>
                                </div>

                                <div>
                                    <label className="text-sm font-medium mb-2 block">Location</label>
                                    <Input
                                        placeholder="Office address or video call link"
                                        value={location}
                                        onChange={(e) => setLocation(e.target.value)}
                                    />
                                </div>

                                <div>
                                    <label className="text-sm font-medium mb-2 block">Notes</label>
                                    <Textarea
                                        placeholder="Questions to ask, things to bring, etc."
                                        value={notes}
                                        onChange={(e) => setNotes(e.target.value)}
                                        rows={3}
                                    />
                                </div>

                                <div className="flex items-center justify-between p-3 rounded-lg bg-muted/50">
                                    <label className="text-sm font-medium">Set reminder</label>
                                    <Switch checked={reminder} onCheckedChange={setReminder} />
                                </div>

                                <div className="flex gap-3">
                                    <Button onClick={saveAppointment} disabled={saving} className="flex-1" data-testid="save-appointment-btn">
                                        <Save className="w-4 h-4 mr-2" />
                                        {saving ? 'Saving...' : editingId ? 'Update' : 'Add Appointment'}
                                    </Button>
                                    <Button variant="outline" onClick={() => { setShowAdd(false); resetForm(); }}>Cancel</Button>
                                </div>
                            </CardContent>
                        </Card>
                    )}

                    {/* Upcoming Appointments */}
                    <Card>
                        <CardHeader>
                            <CardTitle>Upcoming Appointments</CardTitle>
                            <CardDescription>Your scheduled medical visits</CardDescription>
                        </CardHeader>
                        <CardContent>
                            {loading ? (
                                <div className="flex items-center justify-center py-8">
                                    <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-indigo-600"></div>
                                </div>
                            ) : upcomingAppointments.length === 0 ? (
                                <div className="text-center py-8 text-muted-foreground">
                                    <Stethoscope className="w-12 h-12 mx-auto mb-2 opacity-50" />
                                    <p>No upcoming appointments</p>
                                    <Button variant="link" onClick={() => setShowAdd(true)}>Schedule an appointment</Button>
                                </div>
                            ) : (
                                <div className="space-y-4">
                                    {upcomingAppointments.map((apt) => (
                                        <div
                                            key={apt.id}
                                            className="p-4 rounded-xl border bg-muted/30 hover:shadow-md transition-shadow"
                                            data-testid={`appointment-${apt.id}`}
                                        >
                                            <div className="flex items-start justify-between">
                                                <div className="flex-1">
                                                    <div className="flex items-center gap-2 mb-2">
                                                        <Badge className={getStatusColor(apt.date)}>
                                                            {getTimeUntil(apt.date)}
                                                        </Badge>
                                                        {apt.specialty && (
                                                            <Badge variant="outline">{apt.specialty}</Badge>
                                                        )}
                                                    </div>
                                                    <h3 className="font-bold text-lg">{apt.title}</h3>
                                                    <div className="flex flex-wrap gap-3 mt-2 text-sm text-muted-foreground">
                                                        {apt.doctor_name && (
                                                            <div className="flex items-center gap-1">
                                                                <User className="w-3 h-3" />
                                                                {apt.doctor_name}
                                                            </div>
                                                        )}
                                                        <div className="flex items-center gap-1">
                                                            <CalendarIcon className="w-3 h-3" />
                                                            {format(parseISO(apt.date), 'MMM d, yyyy')}
                                                        </div>
                                                        <div className="flex items-center gap-1">
                                                            <Clock className="w-3 h-3" />
                                                            {apt.time}
                                                        </div>
                                                        {apt.location && (
                                                            <div className="flex items-center gap-1">
                                                                <MapPin className="w-3 h-3" />
                                                                {apt.location}
                                                            </div>
                                                        )}
                                                    </div>
                                                    {apt.notes && (
                                                        <p className="text-sm mt-2 p-2 bg-muted/50 rounded">{apt.notes}</p>
                                                    )}
                                                    <AttachmentExpander
                                                        parentType="appointment"
                                                        parentId={apt.id}
                                                        initialCount={attachmentCounts[`appointment:${apt.id}`] || 0}
                                                    />
                                                </div>
                                                <div className="flex gap-2">
                                                    <Button variant="ghost" size="icon" className="h-8 w-8" onClick={() => editAppointment(apt)}>
                                                        <Edit2 className="w-4 h-4" />
                                                    </Button>
                                                    <Button variant="ghost" size="icon" className="h-8 w-8 text-red-500" onClick={() => deleteAppointment(apt.id)}>
                                                        <Trash2 className="w-4 h-4" />
                                                    </Button>
                                                </div>
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            )}
                        </CardContent>
                    </Card>

                    {/* Past Appointments */}
                    {pastAppointments.length > 0 && (
                        <Card className="opacity-75">
                            <CardHeader>
                                <CardTitle className="text-lg">Past Appointments</CardTitle>
                            </CardHeader>
                            <CardContent>
                                <div className="space-y-2">
                                    {pastAppointments.slice(0, 5).map((apt) => (
                                        <div key={apt.id} className="p-3 rounded-lg bg-muted/30">
                                            <div className="flex items-center justify-between">
                                                <div>
                                                    <span className="font-medium">{apt.title}</span>
                                                    <span className="text-sm text-muted-foreground ml-2">
                                                        {format(parseISO(apt.date), 'MMM d, yyyy')}
                                                    </span>
                                                </div>
                                                <Button variant="ghost" size="icon" className="h-8 w-8 text-red-500" onClick={() => deleteAppointment(apt.id)}>
                                                    <Trash2 className="w-4 h-4" />
                                                </Button>
                                            </div>
                                            <AttachmentExpander
                                                parentType="appointment"
                                                parentId={apt.id}
                                                initialCount={attachmentCounts[`appointment:${apt.id}`] || 0}
                                            />
                                        </div>
                                    ))}
                                </div>
                            </CardContent>
                        </Card>
                    )}
                </div>

                <div className="space-y-6">
                    <Card className="bg-gradient-to-br from-sky-50 to-indigo-50 dark:from-sky-950/30 dark:to-indigo-950/20">
                        <CardHeader>
                            <CardTitle className="text-lg">📋 Appointment Prep</CardTitle>
                        </CardHeader>
                        <CardContent className="text-sm space-y-2 text-muted-foreground">
                            <p>• Write down your questions beforehand</p>
                            <p>• Bring list of current medications</p>
                            <p>• Note recent symptoms or changes</p>
                            <p>• Bring insurance cards</p>
                            <p>• Arrive 15 minutes early</p>
                        </CardContent>
                    </Card>

                    <Card className="bg-gradient-to-br from-amber-50 to-orange-50 dark:from-amber-950/30 dark:to-orange-950/20">
                        <CardHeader>
                            <CardTitle className="text-lg">💡 Tips</CardTitle>
                        </CardHeader>
                        <CardContent className="text-sm space-y-2 text-muted-foreground">
                            <p>• Take notes during appointments</p>
                            <p>• Ask for copies of lab results</p>
                            <p>• Request next appointment before leaving</p>
                            <p>• Bring someone for support if needed</p>
                        </CardContent>
                    </Card>
                </div>
            </div>
        </div>
    );
}
